import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './modal.css';
import { classificarMensagem } from '../../utils/classificarMensagem';
import { useMonitoramento } from '../../context/MonitoramentoContext';
import { playAlertSound } from '../../utils/alertSound';
import { parseServerDate } from '../../utils/serverDate';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_823 || '',
  auth: { username: 'Portal', password: 'Portal1163' },
});

function formatarData(dataInput) {
  return parseServerDate(dataInput).toLocaleString('pt-BR');
}

function capitalizeFirstLetter(str) {
  return str.toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
}

function ultimaOcorrencia(records) {
  return records.reduce((max, r) => {
    const d = parseServerDate(r.recepcao);
    return d > max ? d : max;
  }, new Date(0));
}

function formatarRelativo(data) {
  if (isNaN(data.getTime()) || data.getTime() === 0) return '';
  const diffMin = Math.floor((Date.now() - data.getTime()) / 60000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' + data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function buildSnapshot(records) {
  const map = new Map();
  records.forEach((r) => {
    const t = parseServerDate(r.recepcao).getTime();
    const ordem = classificarMensagem(r.mensagem).ordem;
    const atual = map.get(r.cnpj);
    if (!atual) {
      map.set(r.cnpj, { count: 1, last: t, ordem });
    } else {
      atual.count += 1;
      if (t > atual.last) atual.last = t;
      if (ordem < atual.ordem) atual.ordem = ordem;
    }
  });
  return map;
}

const ListaRegistrosDev = ({ filtroNivel }) => {
  const { pdvData } = useMonitoramento();

  const [devRecords, setDevRecords]     = useState([]);
  const [excludedIds, setExcludedIds]   = useState(new Set());
  const [state, setState] = useState({
    erro: false,
    modalIsOpen: false,
    modalData: [],
    filtroProjeto: ' Todos',
    exclusaoEmAndamento: false,
  });

  // Quando o context atualiza, os IDs excluídos já sumiram no servidor — limpa o filtro local
  useEffect(() => {
    setExcludedIds(new Set());
  }, [pdvData]);

  const prodTesteRecords = useMemo(() =>
    (pdvData || [])
      .filter(r => r.cnpj === '11111111111111')
      .map(r => ({ ...r, _source: 'prod' })),
    [pdvData]
  );

  const registrosOriginais = useMemo(() =>
    [...devRecords, ...prodTesteRecords].filter(r => !excludedIds.has(r.controle)),
    [devRecords, prodTesteRecords, excludedIds]
  );

  const [changedIds, setChangedIds] = useState(() => new Set());
  const prevSnapshotRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  useEffect(() => {
    const snapshot = buildSnapshot(registrosOriginais);
    const prev = prevSnapshotRef.current;

    if (prev) {
      const changed = new Set();
      let hasNovaCritica = false;
      snapshot.forEach((info, cnpj) => {
        const antes = prev.get(cnpj);
        if (!antes || info.count > antes.count || info.last > antes.last) {
          changed.add(cnpj);
          if (info.ordem === 1) hasNovaCritica = true;
        }
      });
      if (changed.size > 0) {
        setChangedIds(changed);
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => setChangedIds(new Set()), 4000);
      }
      if (hasNovaCritica) playAlertSound();
    }

    prevSnapshotRef.current = snapshot;
  }, [registrosOriginais]);

  useEffect(() => () => clearTimeout(flashTimeoutRef.current), []);

  const projetos = useMemo(() => {
    const unicos = Array.from(new Set(registrosOriginais.map(r => r.projeto))).filter(Boolean);
    return [' Todos', ...unicos].sort();
  }, [registrosOriginais]);

  const registros = useMemo(() =>
    state.filtroProjeto === ' Todos'
      ? registrosOriginais
      : registrosOriginais.filter(r => r.projeto === state.filtroProjeto),
    [registrosOriginais, state.filtroProjeto]
  );

  const carregarDevRecords = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/dev_exception');
      setDevRecords(response.data.map(r => ({ ...r, _source: 'dev' })));
    } catch {
      setState(prev => ({ ...prev, erro: true }));
    }
  }, []);

  const excluirRegistro = useCallback(async (id, source) => {
    if (source === 'prod') {
      await axiosInstance.post(`/pdv_exception/${id}`, '');
    } else {
      await axiosInstance.get(`/dev_exception_del/${id}`);
    }
  }, []);

  const limparRegistros = useCallback(async (records, nome) => {
    if (!window.confirm(`Limpar ${records.length} registro(s) de "${nome}"?`)) return;
    const ids = records.map(r => r.controle);
    setState(prev => ({ ...prev, exclusaoEmAndamento: true }));
    try {
      for (const record of records) {
        await excluirRegistro(record.controle, record._source);
      }
      setDevRecords(prev => prev.filter(r => !ids.includes(r.controle)));
      setExcludedIds(prev => new Set([...prev, ...ids]));
      setState(prev => ({ ...prev, exclusaoEmAndamento: false }));
    } catch {
      setState(prev => ({ ...prev, exclusaoEmAndamento: false }));
    }
  }, [excluirRegistro]);

  const exibirDetalhes = useCallback((records) => {
    setState(prev => ({ ...prev, modalData: records, modalIsOpen: true }));
  }, []);

  const closeModal = useCallback(() => {
    setState(prev => ({ ...prev, modalIsOpen: false }));
    carregarDevRecords();
  }, [carregarDevRecords]);

  useEffect(() => {
    carregarDevRecords();
    const interval = setInterval(carregarDevRecords, 30_000);
    return () => clearInterval(interval);
  }, [carregarDevRecords]);

  const groupByCnpj = useCallback((records) => {
    const grouped = records.reduce((acc, r) => {
      acc[r.cnpj] = (acc[r.cnpj] || []).concat(r);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([cnpj, recs]) => {
        const classificacoes = recs.map(r => classificarMensagem(r.mensagem));
        const classificacao = classificacoes.reduce((mais, atual) =>
          atual.ordem < mais.ordem ? atual : mais
        );
        return { cnpj, records: recs, count: recs.length, classificacao };
      })
      .sort((a, b) => {
        if (a.classificacao.ordem !== b.classificacao.ordem)
          return a.classificacao.ordem - b.classificacao.ordem;
        return parseServerDate(b.records[0].recepcao) - parseServerDate(a.records[0].recepcao);
      });
  }, []);

  const sortedRegistros = groupByCnpj(registros);
  const visibleRegistros = filtroNivel
    ? sortedRegistros.filter(g => g.classificacao.nivel === filtroNivel)
    : sortedRegistros;

  return (
    <div>
      {state.erro ? (
        <div className="alert alert-danger">Erro ao carregar dados do servidor. Por favor, tente novamente mais tarde.</div>
      ) : (
        <React.Fragment>
          <p className="text-muted small mb-2">Erros disparados em desenvolvimento / teste</p>

          <div className="d-flex flex-wrap gap-2 mb-3">
            {projetos.map((projeto, index) => {
              const records = projeto === ' Todos'
                ? registrosOriginais
                : registrosOriginais.filter(r => r.projeto === projeto);
              const sevs = records.map(r => classificarMensagem(r.mensagem));
              const piorOrdem = sevs.length > 0 ? Math.min(...sevs.map(s => s.ordem)) : 99;
              const pior = sevs.find(s => s.ordem === piorOrdem);
              const badgeClass = pior ? pior.badgeClass : 'bg-secondary';
              const isActive = state.filtroProjeto === projeto;
              return (
                <button
                  key={index}
                  type="button"
                  className={`btn btn-sm ${isActive ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setState(prev => ({ ...prev, filtroProjeto: projeto }))}
                >
                  {capitalizeFirstLetter(projeto)}
                  <span className={`badge ${badgeClass} ms-2`}>{records.length}</span>
                </button>
              );
            })}
          </div>

          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {visibleRegistros.length > 0 ? (
              visibleRegistros.map(({ cnpj, records, count, classificacao }) => (
                <div className="col" key={cnpj}>
                  <div className={`card cs-exception-card cs-sev-${classificacao.sev} h-100${changedIds.has(cnpj) ? ' cs-exception-flash' : ''}`}>
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="card-title mb-0">{records[0].nome}</h5>
                        <span className={`badge ${classificacao.badgeClass} rounded-pill fs-6`}>{count}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="card-subtitle mb-0 text-muted">{cnpj}</h6>
                        <small className="text-muted">{formatarRelativo(ultimaOcorrencia(records))}</small>
                      </div>
                      <p className="card-text text-sm mb-2">
                        <small className="text-muted">{records[0].classe} - {records[0].projeto}</small>
                      </p>
                      <div className={`alert ${classificacao.alertClass} p-2 mb-3`} role="alert">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>Mensagem</strong>
                          <small>{classificacao.tipo} · {classificacao.nivel}</small>
                        </div>
                        <div className="cs-exception-msg" dangerouslySetInnerHTML={{ __html: records[0].mensagem }} />
                      </div>
                      <div className="mt-auto d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => exibirDetalhes(records)}>
                          Detalhes
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          disabled={state.exclusaoEmAndamento}
                          onClick={() => limparRegistros(records, records[0].nome)}
                        >
                          {state.exclusaoEmAndamento ? 'Limpando...' : 'Limpar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col">
                <div className="alert alert-info" role="alert">
                  Nenhum registro encontrado para o filtro selecionado.
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      <Modal
        isOpen={state.modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Detalhes"
        className="react-modal-content"
        overlayClassName="react-modal-overlay"
      >
        <div className="modal-header modal-header-wrapper">
          <h5 className="modal-title">Detalhes dos Registros</h5>
          <button type="button" className="btn-close" aria-label="Fechar" onClick={closeModal} />
        </div>
        {state.modalData.length > 0 ? (
          <div className="modal-table">
            <table className="table table-hover font-sm data-table">
              <thead className="table-header">
                <tr>
                  <th>Classe/ID</th>
                  <th>Erro</th>
                </tr>
              </thead>
              <tbody>
                {state.modalData.map((registro, index) => (
                  <tr key={index}>
                    <td className="limited-cell">
                      <p className="fw-bold mb-0">{registro.classe}</p>
                      <small className="text-muted text-nowrap">{formatarData(registro.recepcao)}</small>
                    </td>
                    <td style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: registro.mensagem }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum registro encontrado.</p>
        )}
      </Modal>
    </div>
  );
};

export default ListaRegistrosDev;
